import * as PIXI from "pixi.js";
import { Spine, Skin } from "@esotericsoftware/spine-pixi-v7";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { Settings } from "../../../settings";

/**
 * GambleLevelOverlay — the live card that replaces the landed strip texture.
 *
 * This is the same split the reels use: a flat texture is good enough while a
 * symbol is moving, but the moment it lands it has to become a real spine so
 * it can animate. ReelControllerV2 spins sprites and OverlayReelController
 * puts a spine over the landed cell; this does exactly that for the one cell
 * inside the gamble scatter.
 *
 * What the live card adds over the texture:
 *   - the level pips lighting up, one animation per pip
 *   - the spin count as a real text object in the skeleton's own slot,
 *     positioned by the `fs_number` bone instead of guessed on top
 *
 * Tier decides both the skin and how many pips light (GDD §6):
 *
 *   tier 1  color/low   purple  Level 0  no pips
 *   tier 3  color/mid   red     Level 2  pips 1-2
 *   tier 5  color/high  gold    Level 4  pips 1-4
 */

const SPINE_NAME = "gamble_level_symbol";

// Level reached per tier — this is the number of pips that light.
//
// GDD §6: the three entry packages start at levels 0, 2 and 4. Levels 1 and 3
// are real but no package begins there — they are only reached by collecting
// four scatters — which is why these step in twos.
export const TIER_LEVELS = { 0: 0, 1: 0, 3: 2, 5: 4 };

// Each tier writes its count into its own text slot, so the artist can style
// purple, red and gold differently without us branching on colour.
const TIER_NUMBER_SLOTS = {
  1: "txt_slot_number_low",
  3: "txt_slot_number_mid",
  5: "txt_slot_number_high",
};

/**
 * Tier → colour skin. THE one table.
 *
 * gambleScatterConfig's GAMBLE_TIERS used to carry its own copy, and two tables
 * saying the same thing is how a tier gets added to one and forgotten in the
 * other. That failure is silent in the worst way: toStaticCell would ask for a
 * capture, `_build` would find no skin, and the card would simply never be
 * built — no error, no picture. Read from here instead.
 */
export const TIER_SKINS = {
  1: "color/low",
  3: "color/mid",
  5: "color/high",
};

// Pip animations, in order. Named exactly as the skeleton exports them.
//
// ONE DOT EACH. The current card's `levelN/on` lights the Nth dot and says
// nothing about the ones before it, so a level-4 package means playing four
// clips. (The older card in spines/reel_symbol/ was cumulative — its level-4
// clip lit all four — which is why anything reading this list must play the
// whole range rather than only the last entry.)
export const PIP_ANIMATIONS = [
  "level_indicator/level1/on",
  "level_indicator/level2/on",
  "level_indicator/level3/on",
  "level_indicator/level4/on",
];

/**
 * The pip clips a tier must play, in order.
 *
 * The single owner of "how many dots are lit for this package". Both the LIVE
 * card and the flat textures captured for the spinning strip read it, so the
 * card the strip spins past and the card that lands cannot disagree.
 *
 * @param   {number} tier Server gamble tier (0/1/3/5).
 * @returns {string[]} animation names, possibly empty.
 */
export const pipAnimationsForTier = (tier) =>
  PIP_ANIMATIONS.slice(0, TIER_LEVELS[tier] ?? 0);

/**
 * How many pips are lit on this card.
 *
 * ── Why this is not always the tier's level ──
 *
 * A card carries its package's conversions as lit pips, and the entry flourish
 * SPENDS them: each one leaves the card and shuts a box (GDD §6). After that
 * the card is empty, and it must stay empty — including in the flat texture
 * the reel keeps underneath it. Baking the lit pips into that texture and
 * nothing else is what made them come back the moment the next spin started
 * and the live card came off.
 *
 * So both states are real and both are captured: red and gold, lit and spent.
 *
 * @param   {number} tier            Server gamble tier (0/1/3/5).
 * @param   {number|null} [override] Explicit count; null means "the tier's".
 * @returns {number} 0..4
 */
export const pipCountFor = (tier, override = null) => {
  const level = TIER_LEVELS[tier] ?? 0;
  if (override === null || override === undefined) return level;
  return Math.min(Math.max(override, 0), level);
};

// ── The number's placement, taken from the skeleton, not chosen ──
//
// The spine renders the count in txt_slot_number_*, which rides bone
// `fs_number`. That bone used to sit at y 20.28, which through the chain
//
//   root -> scale (1.3) -> levels_main -> fs_number
//
// put the number 26.364 skeleton units ABOVE the card's centre — the card
// itself is centred on 0, `gold_bg` 242 x 1.3 = 314.6, the skeleton's whole
// box. On screen that read as the digit riding high in the card instead of
// sitting in it.
//
// The bone is now y 10 in gamble_level_symbol.json — 13 world units, about 4%
// of the card height above centre. 20.28 rode high; a true 0 sat too low,
// because the card's bottom strip is the pip band and the digit reads as
// centred in the space ABOVE it, not in the card's geometry. 4% is where the
// artist's own reference sheet puts it.
//
// `bounce` only keys scalex/scaley on that bone, never translate, so the squash
// plays exactly as before — it simply happens lower in the card now.
//
// The static strip cell has no bones and must reproduce the placement by hand,
// which is what this constant is for. Change the bone and change this, or the
// flat cell and the live card sit in different places. Y is negated for PIXI,
// which is Y-down.
//
// NOTE: there is a second, older copy of this rig in spines/reel_symbol/ that
// NOTHING loads (see assets.js `spineDir`). Edit spines/gamble_level/.
export const FS_NUMBER_OFFSET_Y = 13;
export const FS_NUMBER_BONE_SCALE = 1.3;

// Font size in SKELETON units. Both paths use this same base and let their
// transform do the scaling, so neither can drift from the other.
// The fontSize authored in fontStyles/gamble_level.json. Kept in step with
// that file so scaleStyle's proportional stroke and shadow maths receive the
// size the style was designed at, and scale nothing.
//
// ── DO NOT RAISE THIS TO MAKE THE FS POPUP BIGGER ──
//
// It was pushed to 133 for exactly that reason and it was wrong: this constant
// is the SCG card's own number, and the card is correct as authored. Raising it
// blew the digits up inside the scatter on the reel. The popup has its own
// scale (VALUE_FONT_SCALE in FreeSpinIntroDialogController) — tune that.
export const FS_NUMBER_FONT_SIZE = 76;

// Each tier has its own style — that is why the skeleton exposes three
// separate text slots rather than one. Styles live in fontStyles/gamble_level.
const TIER_STYLE_KEYS = { 1: "low", 3: "mid", 5: "high" };

export const makeNumberTextStyle = (tier) => {
  const key = TIER_STYLE_KEYS[tier];
  const style = FONT_STYLES.gambleLevel?.[key];
  if (!style) {
    console.warn("[GambleLevelOverlay] No font style for tier", tier);
    return new PIXI.TextStyle(
      scaleStyle(FONT_STYLES.winText, FS_NUMBER_FONT_SIZE),
    );
  }
  // Sized in SKELETON units — the bone transform scales it to the screen, so
  // the flat cell and the live card cannot drift apart.
  return new PIXI.TextStyle(scaleStyle(style, FS_NUMBER_FONT_SIZE));
};

class GambleLevelOverlay {
  /**
   * @param {Object}        p
   * @param {PIXI.Container} p.parent     Container the card is added to.
   * @param {Object}        p.item        { tier, value } that was landed.
   * @param {number}        p.cellWidth
   * @param {number}        p.cellHeight
   * @param {number|null}   [p.pips]      How many pips are lit. Defaults to the
   *   tier's own level, which is right everywhere except AFTER the entry
   *   flourish has spent them — see pipCountFor.
   * @param {boolean}       [p.landing]   True when the card is ARRIVING — the
   *   strip has just stopped on it. Off by default, and it has to be: two of
   *   the three callers build this card only to PHOTOGRAPH it, and a capture
   *   taken mid-bounce is not the resting card the strip spins past.
   */
  constructor({
    parent,
    item,
    cellWidth,
    cellHeight,
    pips = null,
    landing = false,
  }) {
    this.pipsOverride = pips;
    this.landing = !!landing;
    this.parent = parent;
    this.item = item;
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;

    this.spine = null;
    this.numberText = null;
    this.destroyed = false;
    this._pipTimers = [];

    this._build();
  }

  _build() {
    const skin = TIER_SKINS[this.item?.tier];
    // A blank outcome has no card at all — the window simply stays empty, so
    // there is nothing to build and nothing to animate.
    if (!skin) return;

    try {
      this.spine = Spine.from(`${SPINE_NAME}-skeleton`, `${SPINE_NAME}-atlas`);
    } catch (error) {
      console.warn("[GambleLevelOverlay] Could not create spine:", error);
      return;
    }
    if (!this.spine) return;

    this.spine.name = "gamble_level_card";

    // ── default + colour, COMBINED ──
    //
    // setSkinByName REPLACES the active skin. The colour skins only carry the
    // background and the four UNLIT dots; everything that lights up — the `on`
    // and `on_add` attachments, `eclipse outline`, `round_glow` — lives in the
    // `default` skin. Setting the colour skin alone therefore stripped the lit
    // art out of the skeleton, and the pip animations had nothing to show.
    //
    // Combining them is the supported way to layer a variant over a base.
    try {
      this._applySkin(skin);
    } catch (e) {
      console.error("[Card] _applySkin threw", e);
    }

    try {
      this._applyScale();
    } catch (e) {
      console.error("[Card] _applyScale threw", e);
    }
    try {
      this._attachNumber();
    } catch (e) {
      console.error("[Card] _attachNumber threw", e);
    }

    this.parent.addChild(this.spine);
    // Landing clip FIRST, pips after — the reels' own order. See _playLanding.
    this._playLanding();
    this._playPips();
  }

  /** Apply `default` with the tier's colour skin layered on top. */
  _applySkin(colourSkinName) {
    const skeletonData = this.spine.skeleton.data;
    const base = skeletonData.findSkin("default");
    const colour = skeletonData.findSkin(colourSkinName);

    if (!colour) {
      console.warn("[GambleLevelOverlay] Unknown skin:", colourSkinName);
      return;
    }

    const combined = new Skin("gamble_level_combined");
    if (base) combined.addSkin(base);
    combined.addSkin(colour);

    this.spine.skeleton.setSkin(combined);
    // Slots only, never setToSetupPose — that resets BONES too.
    this.spine.skeleton.setSlotsToSetupPose();
  }

  /**
   * The SAME rule the spinning strip cell uses. Not similar — the same.
   *
   * This card replaces a flat texture mid-sequence, and the player must not be
   * able to see the swap. That texture is not an approximation of this spine:
   * SpineTextureExtractor captured it FROM this spine, at scale 1, origin
   * centred, with a symmetric transparent margin. So the art inside the
   * texture and the art in the skeleton are the same pixels in the same place,
   * and the two line up exactly when — and only when — both are drawn at the
   * same factor. makeRenderCell draws the sprite at
   * `cellHeight / Settings.SYMBOL_HEIGHT`, so this does too.
   *
   * Do NOT fit this to bounds. Bounds include the glow and the eclipse, which
   * reach well past the card's edge, so fitting them to the cell shrinks the
   * card itself by however much effect art happens to be posed at that moment
   * — a different answer per animation, and a visible jump on landing.
   *
   * Re-applied on resize, which is why this runs from `resize` too.
   */
  _applyScale() {
    this.spine.scale.set(this.cellHeight / Settings.SYMBOL_HEIGHT);
  }

  /**
   * Put the spin count in the skeleton's own text slot.
   *
   * The slot sits on the `fs_number` bone, so the skeleton positions and
   * scales the number for us — which is the whole reason this is worth
   * swapping the flat texture for. The texture version had to guess.
   */
  _attachNumber() {
    const slotName = TIER_NUMBER_SLOTS[this.item.tier];
    if (!slotName || !this.spine.skeleton.findSlot(slotName)) return;

    // No offset and no scale applied here on purpose — the slot's bone
    // supplies both. That is the reference the static cell copies.
    this.numberText = new PIXI.Text(
      String(this.item.value ?? ""),
      makeNumberTextStyle(this.item.tier),
    );
    this.numberText.anchor.set(0.5);

    const holder = new PIXI.Container();
    holder.addChild(this.numberText);
    this.spine.addSlotObject(slotName, holder);
  }

  /**
   * Play the level animation, looped, on track 0.
   *
   * This is the same shape as any idle in the game — OverlayReelController's
   * showIdleAnimation does `state.setAnimation(0, anim, true)` and nothing
   * more. The card has no idle of its own, so its level animation IS the idle.
   *
   * One animation, not four. Each `level_indicator/levelN/on` lights the dot
   * for that level only; the previewer shows level4 with dots 1-3 dark and
   * dot 4 lit.
   *
   * Played ONCE and frozen on its last frame. Looping made the glow pulse
   * over and over, which reads as a pending state rather than a result.
   */
  // ── Every pip, lit, on the first frame ────────────────────────────────
  //
  // Two things are going on here, and they are separate.
  //
  // ALL of them, not the top one. Each `level_indicator/levelN/on` lights the
  // Nth dot and touches nothing before it, so a level-4 package means four
  // clips — the last frame of the level-4 clip alone is one lit dot and three
  // dark ones. They go on tracks of their own because a track holds a single
  // animation and they would otherwise replace each other, which is the same
  // one-lit-dot picture by a different route. Disjoint clips, so separate
  // tracks cannot fight: each keys only its own slots.
  //
  // ALREADY LIT, not lighting up. The card does not arrive here from nothing —
  // it replaces a flat texture captured with these same pips already on (see
  // capturePose in SymbolData), and the player has been watching that texture
  // spin past. Playing the glow now would light dots that are lit, in front of
  // someone who has already seen them. So each entry is placed AT its end:
  // trackTime jumps to the clip's duration instead of running there.
  //
  // trackEnd is pushed out for both reasons at once. A non-looping entry is
  // disposed once it passes trackEnd, and the `on` slots have NO setup
  // attachment — the dot would go dark the instant the track cleared.
  //
  // Applied immediately rather than left to the ticker, or the first rendered
  // frame is the setup pose: a card with no dots at all.
  /**
   * The card's landing clip, on track 0.
   *
   * ── The reels' rule, applied to a strip ──
   *
   * A symbol plays its landing animation when the reel stops, and its win
   * animations come later. That is all this is. The card lands out of a strip
   * instead of a reel, but the strip is the same mechanism — flat textures
   * while it moves, a live spine when it stops — so the landing is the same
   * too. `gamble_level_symbol` carries `bounce` and nothing played it.
   *
   * `bounce` then `static`, queued on ONE track, exactly as showLandingAnimation
   * queues land then idle. This rig has no `idle`; `static` is its resting look,
   * which is the role idle plays there.
   *
   * ── Track 0 ONLY, and never clearTracks ──
   *
   * The pips live on tracks 1-4 and are parked at their last frame with
   * trackEnd pushed out — see _playPips. Clearing tracks the way the reels do
   * would put every dot out. Nothing here needs to: `bounce` keys `fs_number`,
   * `rectangle`, `rectangle2` and `h1_energy_01`, and not one `level_indicator`
   * slot, so the two cannot fight.
   *
   * It opens and closes on the resting picture — every slot it touches starts
   * and ends at alpha 00 and has no setup attachment — so the handover from the
   * flat strip cell is invisible, and so is the one back to `static`. It keys
   * `fs_number`, which is the bone carrying the spin count, so the number
   * bounces with the card rather than hanging still beside it.
   */
  _playLanding() {
    if (!this.landing || !this.spine?.state) return;

    const available = this.spine.skeleton?.data?.animations || [];
    const has = (name) => available.some((a) => a.name === name);
    if (!has("bounce")) return;

    // setAnimation, not addAnimation: it becomes CURRENT now, so the update
    // below applies it. A queued entry is not promoted by a zero-length update
    // — the same trap the Golden cell's _playSequence documents.
    this.spine.state.setAnimation(0, "bounce", false);
    if (has("static")) this.spine.state.addAnimation(0, "static", true, 0);

    // Force the first frame before anything is drawn, exactly as
    // showLandingAnimation does: "Make visible AFTER animation is queued —
    // prevents stale-pose flash."
    this.spine.state.update(0);
    this.spine.state.apply(this.spine.skeleton);
  }

  _playPips() {
    const level = pipCountFor(this.item?.tier, this.pipsOverride);
    if (level <= 0 || !this.spine.state) return;

    const available = this.spine.skeleton.data.animations;

    for (let pip = 0; pip < level; pip++) {
      const name = PIP_ANIMATIONS[pip];
      const clip = available.find((a) => a.name === name);
      if (!clip) {
        console.warn("[GambleLevelOverlay] Missing level animation:", name);
        continue;
      }

      const entry = this.spine.state.setAnimation(pip + 1, clip.name, false);
      if (!entry) continue;
      entry.trackEnd = Number.MAX_VALUE;
      entry.trackTime = clip.duration;
    }

    this.spine.state.update(0);
    this.spine.state.apply(this.spine.skeleton);
    try {
      this.spine.skeleton.updateWorldTransform(
        this.spine.skeleton.physics !== undefined ? 2 : 0,
      );
    } catch {
      try {
        this.spine.skeleton.updateWorldTransform();
      } catch {}
    }
  }

  /**
   * Where pip N is on screen, 1-based.
   *
   * Global, for the same reason LevelProgressController.tearPosition is: the
   * card hangs off a slot of the scatter spine, so its own transform means
   * nothing on its own.
   *
   * @param   {number} pipIndex 1..4
   * @returns {{x:number,y:number}|null}
   */
  pipPosition(pipIndex) {
    const bone = this.spine?.skeleton?.findBone?.(`level${pipIndex}`);
    if (!bone || !this.spine) return null;
    return this.spine.toGlobal(new PIXI.Point(bone.worldX, bone.worldY));
  }

  /**
   * Put pip N out — it has been spent.
   *
   * Each pip owns a track of its own (see _playPips), so emptying that track
   * is the whole job and cannot disturb the others. The `on` slots have NO
   * setup attachment, so an emptied track leaves the dot dark rather than
   * falling back to some other art.
   *
   * @param {number} pipIndex 1..4
   */
  clearPip(pipIndex) {
    const state = this.spine?.state;
    if (!state || pipIndex < 1 || pipIndex > PIP_ANIMATIONS.length) return;
    // Track numbering matches _playPips: pip 1 is track 1.
    state.setEmptyAnimation(pipIndex, 0);
  }

  /** Re-apply the scale rule after a dimension change. */
  resize(cellWidth, cellHeight) {
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;
    if (this.spine) this._applyScale();
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;

    for (const t of this._pipTimers) t?.clear?.();
    this._pipTimers = [];

    if (this.spine) {
      if (this.spine.parent) this.spine.parent.removeChild(this.spine);
      this.spine.destroy({ children: true });
      this.spine = null;
    }
    this.numberText = null;
  }
}

export default GambleLevelOverlay;
